const MEASURED_VALUE_MIN_EXCLUSIVE = 0;
const MEASURED_VALUE_MAX_INCLUSIVE = 120;
const DEFAULT_ALLOWED_FREQUENCIES = [500, 1000, 2000, 4000, 8000, 16000];
const ALLOWED_SELECTED_INTENSITIES = [20, 30, 40, 50, 60, 70];
const MAX_DECIMAL_PLACES = 2;

const isBlank = (value) =>
  value == null || (typeof value === "string" && value.trim() === "");

const toNumber = (value) => {
  if (value === "" || value == null) {
    return NaN;
  }
  return Number(value);
};

const isMeasuredValueValid = (value) => {
  const numericValue = toNumber(value);
  return (
    !Number.isNaN(numericValue) &&
    numericValue > MEASURED_VALUE_MIN_EXCLUSIVE &&
    numericValue <= MEASURED_VALUE_MAX_INCLUSIVE
  );
};

const isFiniteNumber = (value) => {
  const numericValue = toNumber(value);
  return !Number.isNaN(numericValue) && Number.isFinite(numericValue);
};

const hasAtMostTwoDecimalPlaces = (value) => {
  const normalized = String(value ?? "")
    .trim()
    .replace(",", ".");
  const decimalMatch = normalized.match(/^[+-]?\d+(?:\.(\d*))?$/);

  if (decimalMatch) {
    return (decimalMatch[1]?.length ?? 0) <= MAX_DECIMAL_PLACES;
  }

  const numericValue = Number(normalized);
  if (!Number.isFinite(numericValue)) {
    return false;
  }

  const scaledValue = numericValue * 10 ** MAX_DECIMAL_PLACES;
  return Math.abs(scaledValue - Math.round(scaledValue)) < 1e-9;
};

const getMeasuredValueError = (value) => {
  if (!isMeasuredValueValid(value)) {
    return "Wartość musi być liczbą większą od 0 i nie większą niż 120.";
  }

  if (!hasAtMostTwoDecimalPlaces(value)) {
    return "Wartość może mieć maksymalnie 2 miejsca po przecinku.";
  }

  return null;
};

const uniqueSortedNumbers = (values = []) =>
  [...new Set(values.map(Number).filter((value) => !Number.isNaN(value)))].sort(
    (a, b) => a - b,
  );

const includesOnlyAllowedValues = (values, allowedValues) =>
  values.every((value) => allowedValues.includes(value));

const getTableRawValue = (form, frequency, intensity) =>
  form?.tableValues?.[frequency]?.[intensity] ??
  form?.tableValues?.[frequency]?.[String(intensity)] ??
  form?.tableValues?.[String(frequency)]?.[intensity] ??
  form?.tableValues?.[String(frequency)]?.[String(intensity)];

const validateSelectedFrequencies = (
  selectedFrequencies,
  allowedFrequencies,
  errors,
) => {
  const normalizedFrequencies = uniqueSortedNumbers(selectedFrequencies);

  if (normalizedFrequencies.length === 0) {
    errors.selectedFrequencies = "Wybierz co najmniej jedną częstotliwość.";
    return [];
  }

  if (!includesOnlyAllowedValues(normalizedFrequencies, allowedFrequencies)) {
    errors.selectedFrequencies = `Wybrano niedozwolone częstotliwości. Dozwolone: ${allowedFrequencies.join(
      ", ",
    )} Hz.`;
  }

  return normalizedFrequencies;
};

const validateSelectedIntensities = (selectedIntensities, errors) => {
  const normalizedIntensities = uniqueSortedNumbers(selectedIntensities);

  if (normalizedIntensities.length === 0) {
    errors.selectedIntensities = "Wybierz co najmniej jeden poziom.";
    return [];
  }

  if (
    !includesOnlyAllowedValues(
      normalizedIntensities,
      ALLOWED_SELECTED_INTENSITIES,
    )
  ) {
    errors.selectedIntensities =
      "Wybrano niedozwolone poziomy. Dozwolone: 20, 30, 40, 50, 60, 70 dB.";
  }

  return normalizedIntensities;
};

const validateNoStimulusByFrequency = (
  form,
  normalizedFrequencies,
  fieldErrors,
  errors,
) => {
  normalizedFrequencies.forEach((frequency) => {
    const rawValue =
      form?.noStimulusByFrequency?.[frequency] ??
      form?.noStimulusByFrequency?.[String(frequency)];

    const valueError = getMeasuredValueError(rawValue);
    if (valueError) {
      fieldErrors[`noStimulusByFrequency.${frequency}`] = valueError;
    }
  });

  if (
    normalizedFrequencies.some(
      (frequency) => fieldErrors[`noStimulusByFrequency.${frequency}`] != null,
    )
  ) {
    errors.noStimulusByFrequency = "Popraw wartości bez bodźca.";
  }
};

const validateTableValues = (
  form,
  normalizedFrequencies,
  normalizedIntensities,
  fieldErrors,
  errors,
) => {
  let hasAnyRowProblem = false;

  normalizedFrequencies.forEach((frequency) => {
    let hasAtLeastOneFilledValueInRow = false;
    let rowHasInvalidValue = false;

    normalizedIntensities.forEach((intensity) => {
      const rawValue = getTableRawValue(form, frequency, intensity);
      const fieldKey = `tableValues.${frequency}.${intensity}`;

      if (isBlank(rawValue)) {
        return;
      }

      hasAtLeastOneFilledValueInRow = true;

      const valueError = getMeasuredValueError(rawValue);
      if (valueError) {
        fieldErrors[fieldKey] = valueError;
        rowHasInvalidValue = true;
      }
    });

    if (!hasAtLeastOneFilledValueInRow) {
      errors[`tableValuesRow.${frequency}`] =
        "Dla każdej wybranej częstotliwości podaj co najmniej jedną wartość w dowolnej intensywności.";
      hasAnyRowProblem = true;
      return;
    }

    if (rowHasInvalidValue) {
      hasAnyRowProblem = true;
    }
  });

  if (hasAnyRowProblem) {
    errors.tableValues =
      "Dla każdej wybranej częstotliwości podaj co najmniej jedną poprawną wartość w tabeli odsłuchu.";
  }
};

const validateRootFields = (form, isByModel, errors) => {
  if (isBlank(form?.description)) {
    errors.description = "Opis jest wymagany.";
  }

  if (isBlank(form?.location)) {
    errors.location = "Lokalizacja jest wymagana.";
  }

  if (isByModel && isBlank(form?.deviceId)) {
    errors.deviceId = "DeviceId jest wymagane dla odsłuchu BY_MODEL.";
  }

  if (!isFiniteNumber(form?.generalBackgroundIntensity)) {
    errors.generalBackgroundIntensity = "Podaj poprawną wartość tła.";
  } else if (Number(form.generalBackgroundIntensity) === 0) {
    errors.generalBackgroundIntensity = "Wartość tła nie może wynosić 0.";
  } else if (!hasAtMostTwoDecimalPlaces(form.generalBackgroundIntensity)) {
    errors.generalBackgroundIntensity =
      "Wartość może mieć maksymalnie 2 miejsca po przecinku.";
  }

  if (!isFiniteNumber(form?.sonometerError)) {
    errors.sonometerError = "Podaj poprawny błąd sonometru.";
  } else if (!hasAtMostTwoDecimalPlaces(form.sonometerError)) {
    errors.sonometerError =
      "Wartość może mieć maksymalnie 2 miejsca po przecinku.";
  }
};

export const validateListeningForm = ({
  form,
  isByModel,
  allowedFrequencies = DEFAULT_ALLOWED_FREQUENCIES,
}) => {
  const errors = {};
  const fieldErrors = {};

  validateRootFields(form, isByModel, errors);

  const normalizedFrequencies = validateSelectedFrequencies(
    form?.selectedFrequencies,
    uniqueSortedNumbers(allowedFrequencies),
    errors,
  );
  const normalizedIntensities = validateSelectedIntensities(
    form?.selectedIntensities,
    errors,
  );

  if (
    normalizedFrequencies.length > 0 &&
    normalizedIntensities.length > 0 &&
    !errors.selectedFrequencies &&
    !errors.selectedIntensities
  ) {
    validateNoStimulusByFrequency(
      form,
      normalizedFrequencies,
      fieldErrors,
      errors,
    );
    validateTableValues(
      form,
      normalizedFrequencies,
      normalizedIntensities,
      fieldErrors,
      errors,
    );
  }

  return {
    isValid:
      Object.keys(errors).length === 0 && Object.keys(fieldErrors).length === 0,
    errors,
    fieldErrors,
  };
};

export const getListeningFieldError = (fieldErrors, key) =>
  fieldErrors?.[key] || "";

export const hasListeningFieldError = (fieldErrors, key) =>
  Boolean(fieldErrors?.[key]);
